import React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import type {
  FilterControllerProps,
  FilterState as ControllerFilterState,
} from '@atlassian/filter-controller';
import FilterController from '@atlassian/filter-controller';

import { useAppUpdatesContext } from '@app/app/src/AppContext';

import { useBundleDates, useBundleUrlFilterSync } from '../../hooks';
import { toSingleOptions } from '../../utils/filterControllerUtils';
import {
  getBundleProductsFromOptions,
  useBundleProductFilterConfig,
} from '../../filter-config/bundles/BundleProductFilterConfig';
import {
  getBundleDateFromOptions,
  toBundleDateOption,
  useBundleDateFilterConfig,
} from '../../filter-config/bundles/BundleDateFilterConfig';

const messages = defineMessages({
  searchLabel: {
    id: 'appUpdates.bundles.search.label',
    defaultMessage: 'Search release notes',
    description: 'Accessibility label for bundled changes search input',
  },
  searchPlaceholder: {
    id: 'appUpdates.bundles.search.placeholder',
    defaultMessage: 'Search release notes',
    description: 'Placeholder text for bundled changes search input',
  },
});

export const BundledChangesFilters: React.FC = () => {
  const { formatMessage } = useIntl();
  const { state, dispatch } = useAppUpdatesContext();
  const { syncFiltersToUrl } = useBundleUrlFilterSync();
  const { latestBundleDate } = useBundleDates(null);
  const [filterControllerKey, setFilterControllerKey] = React.useState(0);
  const shouldIgnoreNextApplyRef = React.useRef(false);

  const productFilterConfig = useBundleProductFilterConfig();
  const dateFilterConfig = useBundleDateFilterConfig();
  const { draftFilters } = state.bundles;

  const filters = React.useMemo<FilterControllerProps['filters']>(
    () => [
      productFilterConfig,
      {
        ...dateFilterConfig,
        isMulti: false,
        defaultValue: latestBundleDate
          ? [toBundleDateOption(latestBundleDate)]
          : [],
      },
    ],
    [dateFilterConfig, latestBundleDate, productFilterConfig],
  );

  const handleApply = React.useCallback(
    (values: ControllerFilterState) => {
      const selectedProducts = getBundleProductsFromOptions(
        toSingleOptions(values.filters[productFilterConfig.fieldId]),
      );
      const selectedBundleDate = getBundleDateFromOptions(
        toSingleOptions(values.filters[dateFilterConfig.fieldId]),
      );
      const searchQuery = values.searchText || null;

      dispatch({
        type: 'BUNDLE_FILTER_APP_SELECT',
        payload: { selectedProducts },
      });
      dispatch({
        type: 'BUNDLE_FILTER_DATE_SELECT',
        payload: { selectedBundleDate },
      });
      dispatch({
        type: 'BUNDLE_FILTER_SEARCH_SELECT',
        payload: { searchQuery },
      });

      syncFiltersToUrl({
        selectedProducts,
        selectedBundleDate,
        searchQuery,
        cursor: undefined,
      });

      dispatch({ type: 'BUNDLES_FILTERS_APPLY' });
    },
    [
      dateFilterConfig.fieldId,
      dispatch,
      productFilterConfig.fieldId,
      syncFiltersToUrl,
    ],
  );

  const handleReset = React.useCallback(() => {
    shouldIgnoreNextApplyRef.current = true;

    syncFiltersToUrl({
      selectedProducts: null,
      selectedBundleDate: latestBundleDate,
      searchQuery: null,
      cursor: undefined,
    });

    dispatch({
      type: 'BUNDLES_FILTERS_RESET',
      payload: { selectedBundleDate: latestBundleDate },
    });
    setFilterControllerKey((currentKey) => currentKey + 1);
  }, [dispatch, latestBundleDate, syncFiltersToUrl]);

  const handleFilterControllerApply = React.useCallback(
    (values: ControllerFilterState) => {
      if (shouldIgnoreNextApplyRef.current) {
        shouldIgnoreNextApplyRef.current = false;
        return;
      }

      handleApply(values);
    },
    [handleApply],
  );

  return (
    <FilterController
      key={filterControllerKey}
      testId="app-updates-bundle-filters"
      filters={filters}
      isManual
      hideItemsCountFooter
      itemsCount={state.bundles.data.length}
      onApply={handleFilterControllerApply}
      onReset={handleReset}
      searchInput={draftFilters.searchQuery ?? ''}
      searchInputAriaLabel={formatMessage(messages.searchLabel)}
      searchPlaceholder={formatMessage(messages.searchPlaceholder)}
    />
  );
};
